import analytics from './analytics';
import appOptions from './appOptions';
import constants from './constants';
import utility from './utility';
import nativeAnalytics from './nativeAnalytics';

const PICKETLINE_EVENTS = {
    CHECKOUT_INITIATED: 'checkout.initiated',
    CHECKOUT_PRESENTED: 'checkout.presented',
    // House Rules
    ACCEPT_HOUSE_RULES_SELECTED: 'accept_house_rules.selected',
    ACCEPT_TERMS_SELECTED: 'accept_terms.selected',
    // Payment
    BOOKING_CONFIRMATION_PRESENTED: 'booking_confirmation.presented',
    BOOKING_FAILURE_PRESENTED: 'booking_failure.presented',
    BOOKING_PAYMENT_METHOD_FAILED: 'booking_payment_method.failed',
    BOOKING_PAYMENT_METHOD_SUCCEEDED: 'booking_payment_method.succeeded',
    BOOKING_REQUEST_FAILED: 'booking_request.failed',
    BOOKING_REQUEST_INITIATED: 'booking_request.initiated',
    BOOKING_REQUEST_SUBMITTED: 'booking_request.submitted',
    BOOKING_REQUEST_SUCCEEDED: 'booking_request.succeeded',
    BOOKING_VALIDATION_ERROR_PRESENTED: 'booking_validation_error.presented',
    FLEXPAY_DISMISSED: 'flexpay.dismissed',
    FLEXPAY_PRESENTED: 'flexpay.presented',
    FLEXPAY_SELECTED: 'flexpay.selected',
    PAYMENT_METHOD_PRESENTED: 'payment_method.presented',
    PAYMENT_METHOD_SELECTED: 'payment_method.selected',
    PAYMENT_VALIDATION_ERROR: 'payment.validation.error',
    PRICE_QUOTE_PRESENTED: 'price_quote.presented',
};

const {
    EDAP_CATEGORIES: {CHECKOUT, PRICE_DETAILS},
} = constants;

// Picketline events share payload logic between web + native webview.
// Extracting these methods out here to consolidate logic and provide a single entry point.
const picketlineAnalytics = {
    // Helper method to handle calling nativeAnalytics vs web analytics
    trackEvent(event, properties) {
        if (nativeAnalytics.isEnabled()) {
            nativeAnalytics.trackPicketlineEvent(event, properties);
            // Ensure we do not duplicate events between web + native
            return;
        }

        // decorate the picketline event with the platform_source
        properties = {
            ...properties,
            platform_source: utility.getPlatformSource(),
        };

        analytics.analyticsEvent(event, properties);
    },
    // EVENT-41: should trigger when user wants to receive email marketing communication and clicks the Continue button on the contact page
    // web only event currently
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/accept_terms-selected
    acceptTermsSelected(checkboxState, optInType) {
        const {isOptIn, isOptOut} = checkboxState;

        let marketingEmails = 'opt_in';

        if (isOptOut && isOptIn) {
            return;
        }

        if (optInType === 'OPT_IN' && !isOptIn) {
            // au brands, explicit opt-in required
            marketingEmails = 'opt_out';
        }

        if (optInType === 'SOFT_OPT_IN' && isOptOut) {
            // eu brands, users can opt-out
            marketingEmails = 'opt_out';
        }

        const properties = {
            action_location: CHECKOUT,
            marketing_email: marketingEmails,
            item4: utility.getItem4Param(),
        };

        if (
            nativeAnalytics.isEnabled() &&
            typeof window.Android.acceptTermsSelected === 'function'
        ) {
            // backwards compatibility
            window.Android.acceptTermsSelected();
        }
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.ACCEPT_TERMS_SELECTED,
            properties
        );
    },

    // EVENT-32: should fire when the VAS interstitial screen shows up
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_confirmation-presented
    bookingConfirmationPresented(
        paymentSplit,
        isSquashPay,
        paymentMethod,
        lbsUuid
    ) {
        analytics.analyticsEvent(
            PICKETLINE_EVENTS.BOOKING_CONFIRMATION_PRESENTED,
            {
                action_location: CHECKOUT,
                payment_split: paymentSplit || '-1',
                payment_squash: isSquashPay || false,
                payment_method: paymentMethod || '-1',
                listing_id:
                    appOptions.get('unitIdentity') ||
                    appOptions.get('listingId') ||
                    '-1',
                lbsuuid: lbsUuid || '-1',
            }
        );
    },
    // DEPRECATED: Please use use-picketline-analytics
    // EVENT-32: should be triggered when API call to submit and a booking request returns an error
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_request-failed
    bookingRequestFailed(
        violations = [],
        paymentSplit,
        isSquashPay,
        paymentMethod
    ) {
        // originalErrorCode - originalErrorCode is for situations where the key get overwritten by the complete-violations-transfromer
        // key - key is the original error code we get from graphql-traveler-checkout partial from checkout-service
        // code - code is the legacy AOE error code we get from checkoutv2 endpoint
        const errorCode =
            (Array.isArray(violations) &&
                violations[0] &&
                (violations[0].originalErrorCode ||
                    violations[0].key ||
                    violations[0].code)) ||
            '-1';
        const properties = {
            action_location: CHECKOUT,
            error_code: errorCode,
            payment_split: paymentSplit || '-1',
            payment_squash: isSquashPay || false,
            payment_method: paymentMethod || '-1',
            booking_flow: appOptions.get('checkoutType'),
            item4: utility.getItem4Param(),
            vrbex: appOptions.get('vrbex'),
        };
        if (
            nativeAnalytics.isEnabled() &&
            typeof window.Android.bookingRequestFailed === 'function'
        ) {
            // backwards compatibility
            window.Android.bookingRequestFailed();
        }
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_REQUEST_FAILED,
            properties
        );
    },

    // EVENT-32: should be triggered when API call to submit a booking request returns without error
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_request-succeeded
    bookingRequestSucceeded(
        reservationId,
        tripId,
        paymentSplit,
        isSquashPay,
        paymentMethod,
        priceDetails,
        loyaltyId,
        partnerName,
        bidTarget
    ) {
        const {discountApplied, discountLabel} =
            utility.getTSFDiscountEdapData(priceDetails);
        const {fullorderrentalrate, ordertax, displayquotetotal} =
            (priceDetails?.edapQuoteSuccess &&
                JSON.parse(priceDetails.edapQuoteSuccess)) ||
            {};
        const displayQuoteTotalCurrency =
            priceDetails?.edapPriceQuotePresented &&
            JSON.parse(priceDetails.edapPriceQuotePresented)
                ?.display_quote_total_currency;
        const orderTotal = utility.calculateOrderTotal(
            ordertax,
            displayquotetotal
        );
        let properties = {
            action_location: CHECKOUT,
            adult_count:
                // eslint-disable-next-line eqeqeq,no-eq-null
                appOptions.get('adults') != null
                    ? appOptions.get('adults')
                    : '-1',
            bid_target: bidTarget,
            booking_flow: appOptions.get('checkoutType') || '-1',
            child_count:
                // eslint-disable-next-line eqeqeq,no-eq-null
                appOptions.get('children') != null
                    ? appOptions.get('children')
                    : '-1',
            currency: displayQuoteTotalCurrency || '-1',
            date_end: appOptions.get('checkoutDate') || '-1',
            date_start: appOptions.get('checkinDate') || '-1',
            discount_applied: discountApplied ? discountApplied : '-1',
            discount_label: discountLabel ? discountLabel : '[]',
            fullorderrentalrate,
            // eslint-disable-next-line no-eq-null,eqeqeq,no-undefined
            ordertotal: orderTotal != null ? `${orderTotal}` : undefined,
            listing_id:
                appOptions.get('unitIdentity') || appOptions.get('listingId'),
            loyalty_id: loyaltyId || '-1',
            partner_name: partnerName || '-1',
            payment_method: paymentMethod || '-1',
            payment_split: paymentSplit || '-1',
            payment_squash: isSquashPay || false,
            reservation_id: reservationId || '-1',
            trip_id: tripId || '-1',
            item4: utility.getItem4Param(),
            vrbex: appOptions.get('vrbex'),
        };
        if (
            nativeAnalytics.isEnabled() &&
            typeof window.Android.bookingRequestSucceeded === 'function'
        ) {
            // backwards compatibility
            window.Android.bookingRequestSucceeded();
        }
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_REQUEST_SUCCEEDED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/price_quote-presented
    priceQuotePresented() {
        // native only event currently
        if (nativeAnalytics.isDisabled()) {
            return;
        }

        const properties = {
            item4: utility.getItem4Param(),
        };
        const {PRICE_QUOTE_PRESENTED} = PICKETLINE_EVENTS;
        nativeAnalytics.trackPicketlineEvent(PRICE_QUOTE_PRESENTED, properties);
        // backwards compatibility
        if (typeof window.Android.priceQuotePresented === 'function') {
            window.Android.priceQuotePresented();
        }
    },
    // CHK-8320: Event should be triggered whenever the flex pay modal is open
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/flexpay-presented
    flexPayPresented(enteringFromCheckout) {
        const properties = {
            action_location: CHECKOUT,
            ux_component: enteringFromCheckout ? PRICE_DETAILS : CHECKOUT,
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.FLEXPAY_PRESENTED,
            properties
        );
    },

    // CHK-8320: Event should be triggered whenever the flex pay modal is closed via exit
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/flexpay-dismissed
    flexPayDismissed(enteringFromCheckout) {
        const properties = {
            action_location: CHECKOUT,
            ux_component: enteringFromCheckout ? PRICE_DETAILS : CHECKOUT,
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.FLEXPAY_DISMISSED,
            properties
        );
    },

    // CHK-8320: Event should be triggered whenever user confirms a new payment selection
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/flexpay-selected
    flexPaySelected(enteringFromCheckout, paymentId) {
        const properties = {
            action_location: CHECKOUT,
            ux_component: enteringFromCheckout ? PRICE_DETAILS : CHECKOUT,
            payment_id: paymentId,
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.FLEXPAY_SELECTED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_validation_error-presented
    bookingValidationErrorPresented(validationError) {
        const properties = {
            action_location: CHECKOUT,
            validation_error: validationError || '-1',
            item4: utility.getItem4Param(),
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_VALIDATION_ERROR_PRESENTED,
            properties
        );
    },

    // EGCKS-3273: Called after initialization of checkout before any service calls are made
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/checkout-initiated
    checkoutInitiated() {
        const properties = {
            booking_flow: appOptions.get('checkoutType'),
            vrbex: appOptions.get('vrbex'),
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.CHECKOUT_INITIATED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/checkout-presented
    checkoutPresented(model) {
        const {unitLink} = model.get('quote');
        const {discountApplied, discountLabel} = utility.getTSFDiscountEdapData(
            model.get('priceDetails')
        );
        const cancellationPolicyType =
            model.get('cancellationPolicy')?.policyType;
        const listing = model.get('listing');
        const cancellationPolicy =
            constants.EDAP_CANCELLATION_POLICY_TYPES[cancellationPolicyType];
        let properties = {
            action_location: CHECKOUT,
            adult_count:
                // eslint-disable-next-line eqeqeq,no-eq-null
                appOptions.get('adults') != null
                    ? appOptions.get('adults')
                    : '-1',
            child_count:
                // eslint-disable-next-line eqeqeq,no-eq-null
                appOptions.get('children') != null
                    ? appOptions.get('children')
                    : '-1',
            date_end: appOptions.get('checkoutDate'),
            date_start: appOptions.get('checkinDate'),
            listing_id: appOptions.get('unitIdentity'),
            with_pets: appOptions.get('petIncluded'),
            cancellation_policy: cancellationPolicy
                ? cancellationPolicy
                : constants.EDAP_CANCELLATION_POLICY_TYPES.OTHER,
            discount_applied: discountApplied ? discountApplied : '-1',
            discount_label: discountLabel ? discountLabel : '[]',
            unit_link: unitLink ? unitLink : '-1',
            item4: utility.getItem4Param(),
            booking_flow: appOptions.get('checkoutType'),
            vrbex: appOptions.get('vrbex'),
            lodging_directory_id: listing.lodgingDirectoryId || '',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.CHECKOUT_PRESENTED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_failure-presented
    bookingFailurePresented(errorCode) {
        const properties = {
            action_location: CHECKOUT,
            error_code: errorCode || '-1',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_FAILURE_PRESENTED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/payment_method-presented
    paymentMethodPresented(paymentMethod) {
        const properties = {
            action_location: CHECKOUT,
            listing_id:
                appOptions.get('unitIdentity') ||
                appOptions.get('listingId') ||
                '-1',
            payment_method: paymentMethod?.toLowerCase() || '-1',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.PAYMENT_METHOD_PRESENTED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/payment_method-selected
    paymentMethodSelected(paymentMethod) {
        const properties = {
            action_location: CHECKOUT,
            listing_id:
                appOptions.get('unitIdentity') ||
                appOptions.get('listingId') ||
                '-1',
            payment_method: paymentMethod?.toLowerCase() || '-1',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.PAYMENT_METHOD_SELECTED,
            properties
        );
    },
    // DEPRECATED: Please use use-picketline-analytics
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_request-initiated
    bookingRequestInitiated() {
        const properties = {
            booking_flow: appOptions.get('checkoutType'),
            vrbex: appOptions.get('vrbex'),
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_REQUEST_INITIATED,
            properties
        );
    },
    // DEPRECATED: Please use use-picketline-analytics
    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_request-submitted
    bookingRequestSubmitted(
        paymentSplit,
        isSquashPay,
        paymentMethod,
        cancellationPolicyType,
        priceDetails
    ) {
        const cancellationPolicy =
            constants.EDAP_CANCELLATION_POLICY_TYPES[cancellationPolicyType];
        const {discountApplied, discountLabel} =
            utility.getTSFDiscountEdapData(priceDetails);
        let properties = {
            action_location: CHECKOUT,
            listing_id:
                appOptions.get('unitIdentity') ||
                appOptions.get('listingId') ||
                '-1',
            payment_split: paymentSplit || '-1',
            payment_squash: isSquashPay || false,
            payment_method: paymentMethod?.toLowerCase() || '-1',
            cancellation_policy: cancellationPolicy
                ? cancellationPolicy
                : constants.EDAP_CANCELLATION_POLICY_TYPES.OTHER,
            discount_applied: discountApplied ? discountApplied : '-1',
            discount_label: discountLabel ? discountLabel : '[]',
            item4: utility.getItem4Param(),
            vrbex: appOptions.get('vrbex'),
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_REQUEST_SUBMITTED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_payment_method-succeeded
    bookingPaymentMethodSucceeded(paymentMethod) {
        const properties = {
            action_location: CHECKOUT,
            listing_id:
                appOptions.get('unitIdentity') ||
                appOptions.get('listingId') ||
                '-1',
            payment_method: paymentMethod?.toLowerCase() || '-1',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_PAYMENT_METHOD_SUCCEEDED,
            properties
        );
    },

    // https://pages.github.expedia.biz/vrbo-data-capture/adl-json-schema/specs/latest/events/booking_payment_method-failed
    bookingPaymentMethodFailed(paymentMethod, errorCode) {
        const properties = {
            action_location: CHECKOUT,
            listing_id:
                appOptions.get('unitIdentity') ||
                appOptions.get('listingId') ||
                '-1',
            payment_method: paymentMethod?.toLowerCase() || '-1',
            error_code: errorCode || '-1',
        };
        picketlineAnalytics.trackEvent(
            PICKETLINE_EVENTS.BOOKING_PAYMENT_METHOD_FAILED,
            properties
        );
    },
};

export default picketlineAnalytics;
